// Admin → Demo. Launches the scripted autopilot demos in the retailer or
// customer portal (opens them with ?demo=… which the portal shells pick up).

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

function isDev(): boolean {
  const h = window.location.hostname;
  return h.startsWith("dev") || h.includes("dev-") || h.includes("localhost") || h === "127.0.0.1";
}
function retailerBase(): string { return isDev() ? "https://dev.tkos.io" : "https://thekitchenos.io"; }
function customerBase(): string { return isDev() ? "https://dev-customer.tkos.io" : "https://customer.tkos.io"; }

const demoPage: Page = {
  name: "demo",
  label: "Demo",
  icon: "🎬",
  mount(container: HTMLElement, _ctx: PageContext): PageCleanup {
    const root = document.createElement("section");
    root.className = "adm-demo";
    root.innerHTML = `
      <style>
        .adm-demo{padding:1.5rem;max-width:680px}
        .adm-demo h1{margin:0 0 .35rem;color:var(--adm-text-strong);font-size:1.5rem}
        .adm-demo p.sub{margin:0 0 1.25rem;color:var(--adm-muted);font-size:.9rem;line-height:1.5}
        .adm-demo__grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem}
        .adm-demo__card{background:var(--adm-card);border:1px solid var(--adm-border);border-radius:12px;padding:1.1rem;display:flex;flex-direction:column;gap:.5rem}
        .adm-demo__card h2{margin:0;font-size:1.05rem;color:var(--adm-text-strong)}
        .adm-demo__card p{margin:0;color:var(--adm-muted);font-size:.82rem;line-height:1.45;flex:1}
        .adm-demo__btn{margin-top:.5rem;border:0;border-radius:10px;background:var(--adm-primary);color:#1a1206;font-weight:800;padding:.6rem;cursor:pointer;font-size:.9rem}
        .adm-demo__note{margin-top:1rem;color:var(--adm-muted);font-size:.76rem}
        @media(max-width:640px){.adm-demo__grid{grid-template-columns:1fr}}
      </style>
      <h1>🎬 Demo Mode</h1>
      <p class="sub">Launch a self-running, narrated walkthrough overlaid on the live portal UI. Pure simulation — it never touches real data, customers or payments. Play / pause / skip / speed controls appear at the top; press Exit to return to normal.</p>
      <div class="adm-demo__grid">
        <div class="adm-demo__card">
          <h2>Retailer Demo</h2>
          <p>"A Day in the Life" — 12 steps (~4 min). Website lead → Kit chases it → quote → accepted → deposit → delivery → snag → pipeline summary.</p>
          <button type="button" class="adm-demo__btn" data-portal="retailer">▶ Launch Retailer Demo</button>
        </div>
        <div class="adm-demo__card">
          <h2>Customer Demo</h2>
          <p>"Your Kitchen Journey" — 8 steps (~3 min). Aria on the website → qualifies → wizard handoff → portal → quote → pay → 24/7 support.</p>
          <button type="button" class="adm-demo__btn" data-portal="customer">▶ Launch Customer Demo</button>
        </div>
      </div>
      <p class="adm-demo__note">Opens in a new tab. The demo overlay is entirely client-side and resets on exit.</p>`;
    container.appendChild(root);

    const onClick = (e: MouseEvent): void => {
      const portal = (e.target as HTMLElement).closest<HTMLElement>("[data-portal]")?.dataset["portal"];
      if (portal === "retailer") window.open(`${retailerBase()}/?demo=retailer`, "_blank", "noopener,noreferrer");
      else if (portal === "customer") window.open(`${customerBase()}/?demo=customer`, "_blank", "noopener,noreferrer");
    };
    root.addEventListener("click", onClick);
    return () => { root.removeEventListener("click", onClick); root.remove(); };
  },
};

registerPage(demoPage);
export default demoPage;
